/**
 * KpiDrilldownDrawer — answers "which jobs/crews are dragging this metric?"
 * for any of the four Outcome KPIs. Pure read surface, deep-links to /jobs/:id.
 *
 * Reuses derived data from the same engines already feeding OutcomeKpiStrip:
 *   • capacityEngine   — crew rows + utilization
 *   • jobs / wraps      — for travel, reschedule, FTC drill-downs
 *
 * Phase B additions:
 *   • Width tightened to 380px so it doesn't crowd the cockpit grid.
 *   • Each row now offers a "Focus in grid" action that dispatches
 *     `koredor:focus-grid` (consumed by PlanCockpit) and closes the drawer.
 *     Falls back: cell → crew row → no-op (subtle toast) when the row has
 *     no scheduled date or assigned crew.
 *   • Reschedule rows enriched with the current scheduled date + crew.
 *   • FTC description lists the four explicit miss outcomes for clarity.
 */
import { useMemo } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Link } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { buildCapacityGrid } from "@/data/scheduling/capacityEngine";
import { parseISO, isValid, startOfDay, subDays, format } from "date-fns";
import type { OutcomeMetric } from "@/data/scheduling/outcomeMetrics";
import { ArrowRight, Crosshair } from "lucide-react";
import { toast as sonnerToast } from "sonner";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onClose: () => void;
  metricKey: OutcomeMetric["key"] | null;
}

// Dispatch the cross-component bridge consumed by PlanCockpit. Closes the
// drawer first so the scroll target isn't hidden behind the sheet overlay.
function focusInGrid(
  target:
    | { kind: "cell"; crewName: string; dateIso: string }
    | { kind: "row"; crewName: string }
    | { kind: "none"; reason: string },
  onClose: () => void,
) {
  if (target.kind === "none") {
    sonnerToast("Nothing to focus", { description: target.reason, duration: 3000 });
    return;
  }
  onClose();
  // Defer so the sheet close animation begins before we scroll.
  window.setTimeout(() => {
    window.dispatchEvent(new CustomEvent("koredor:focus-grid", { detail: target }));
  }, 60);
}

export function KpiDrilldownDrawer({ open, onClose, metricKey }: Props) {
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-[380px] overflow-y-auto">
        {metricKey === "utilization" && <UtilizationDrilldown onClose={onClose} />}
        {metricKey === "travel_share" && <TravelDrilldown onClose={onClose} />}
        {metricKey === "reschedule" && <RescheduleDrilldown onClose={onClose} />}
        {metricKey === "first_time_complete" && <FtcDrilldown onClose={onClose} />}
        {metricKey === null && (
          <SheetHeader>
            <SheetTitle>Select a KPI</SheetTitle>
          </SheetHeader>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ── Utilization: under/overbooked crews ───────────────────────────────────
function UtilizationDrilldown({ onClose }: { onClose: () => void }) {
  const { jobs, issues, contractors, opportunities, credits, buildReviewEvents } = useAppData();
  const grid = useMemo(
    () => buildCapacityGrid({
      jobs, issues, contractors,
      contractorOpportunities: opportunities,
      credits, buildReviewEvents,
    }),
    [jobs, issues, contractors, opportunities, credits, buildReviewEvents],
  );

  const sorted = useMemo(() =>
    [...grid.rows].sort((a, b) => a.utilizationPct - b.utilizationPct),
    [grid.rows],
  );
  const under = sorted.filter(r => r.utilizationPct < 60);
  const over = sorted.filter(r => r.utilizationPct > 100);
  const balanced = sorted.filter(r => r.utilizationPct >= 60 && r.utilizationPct <= 100);

  return (
    <>
      <SheetHeader>
        <SheetTitle className="text-base">Crew utilization</SheetTitle>
        <SheetDescription className="text-xs">
          This week's load by crew. Target 75–100%.
        </SheetDescription>
      </SheetHeader>
      <div className="mt-6 space-y-5">
        <CrewBucket title="Overbooked" tone="bad" rows={over} onClose={onClose} />
        <CrewBucket title="Underbooked" tone="warn" rows={under} onClose={onClose} />
        <CrewBucket title="Balanced" tone="good" rows={balanced} onClose={onClose} />
      </div>
    </>
  );
}

function CrewBucket({
  title, tone, rows, onClose,
}: {
  title: string;
  tone: "good" | "warn" | "bad";
  rows: { crewName: string; crewType: string; bookedDays: number; capacityDays: number; utilizationPct: number }[];
  onClose: () => void;
}) {
  const dotClass = tone === "bad" ? "bg-destructive" : tone === "warn" ? "bg-warning" : "bg-success";
  return (
    <section>
      <p className="text-[10px] uppercase tracking-[0.16em] font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
        <span className={cn("h-1.5 w-1.5 rounded-full", dotClass)} />
        {title} ({rows.length})
      </p>
      {rows.length === 0 ? (
        <p className="text-[11px] text-muted-foreground/70 italic">None.</p>
      ) : (
        <ul className="space-y-1">
          {rows.map(r => (
            <li
              key={r.crewName}
              className="flex items-center justify-between gap-3 px-3 py-2 border border-border/40 rounded-md"
            >
              <div className="min-w-0">
                <p className="text-[12px] font-medium text-foreground truncate">{r.crewName}</p>
                <p className="text-[10.5px] text-muted-foreground">
                  {r.crewType === "Subcontractor" ? "Sub" : "Internal"}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[11px] font-medium tabular-nums">
                  {r.bookedDays}/{r.capacityDays} · {r.utilizationPct}%
                </span>
                <FocusButton
                  onClick={() => focusInGrid({ kind: "row", crewName: r.crewName }, onClose)}
                  label="Focus crew row"
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ── Travel share: jobs scheduled outside crew home city ──────────────────
function TravelDrilldown({ onClose }: { onClose: () => void }) {
  const { jobs } = useAppData();
  const list = useMemo(() => {
    const windowStart = startOfDay(subDays(new Date(), 90));
    // Crew home city = most-frequent city in window.
    const cityByCrew = new Map<string, Map<string, number>>();
    for (const j of jobs) {
      const crew = j.assignedCrew || j.assignedSubcontractor;
      if (!crew || !j.address?.city || !j.scheduledInstallDate) continue;
      const d = parseISO(j.scheduledInstallDate);
      if (!isValid(d) || d < windowStart) continue;
      const m = cityByCrew.get(crew) || new Map<string, number>();
      m.set(j.address.city, (m.get(j.address.city) || 0) + 1);
      cityByCrew.set(crew, m);
    }
    const homeCity = new Map<string, string>();
    for (const [crew, cities] of cityByCrew) {
      let home = "", max = 0;
      for (const [c, n] of cities) {
        if (n > max) { max = n; home = c; }
      }
      homeCity.set(crew, home);
    }
    return jobs
      .filter(j => {
        const crew = j.assignedCrew || j.assignedSubcontractor;
        if (!crew || !j.scheduledInstallDate) return false;
        const d = parseISO(j.scheduledInstallDate);
        if (!isValid(d) || d < windowStart) return false;
        const home = homeCity.get(crew);
        return home && j.address.city !== home;
      })
      .map(j => ({
        job: j,
        crew: j.assignedCrew || j.assignedSubcontractor!,
        home: homeCity.get(j.assignedCrew || j.assignedSubcontractor!)!,
      }))
      .slice(0, 30);
  }, [jobs]);

  return (
    <>
      <SheetHeader>
        <SheetTitle className="text-base">Travel share</SheetTitle>
        <SheetDescription className="text-xs">
          Jobs scheduled outside their crew's home city · 90d.
        </SheetDescription>
      </SheetHeader>
      <div className="mt-6">
        {list.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">All recent jobs are in-area. Nice.</p>
        ) : (
          <ul className="space-y-1">
            {list.map(({ job, crew, home }) => (
              <JobRow
                key={job.id}
                jobId={job.id}
                title={job.customerName}
                sub={`${crew} (home: ${home})`}
                tag={`→ ${job.address.city}`}
                tagTone="warn"
                focusTarget={resolveFocusTarget(job.scheduledInstallDate, crew)}
                onClose={onClose}
              />
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

// ── Reschedule rate: moved jobs ──────────────────────────────────────────
function RescheduleDrilldown({ onClose }: { onClose: () => void }) {
  const { jobs } = useAppData();
  const list = useMemo(() => {
    const windowStart = startOfDay(subDays(new Date(), 90));
    return jobs
      .filter(j => {
        if (!j.scheduledInstallDate) return false;
        const d = parseISO(j.scheduledInstallDate);
        if (!isValid(d) || d < windowStart) return false;
        return (j.activityHistory || []).some(a => /reschedul/i.test(a.action || ""));
      })
      .map(j => {
        const events = (j.activityHistory || []).filter(a => /reschedul/i.test(a.action || ""));
        return { job: j, count: events.length };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 30);
  }, [jobs]);

  return (
    <>
      <SheetHeader>
        <SheetTitle className="text-base">Reschedule rate</SheetTitle>
        <SheetDescription className="text-xs">
          Jobs whose install date moved at least once · 90d. Source: job activity history.
        </SheetDescription>
      </SheetHeader>
      <div className="mt-6">
        {list.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">No reschedules in window.</p>
        ) : (
          <ul className="space-y-1">
            {list.map(({ job, count }) => {
              const crew = job.assignedCrew || job.assignedSubcontractor || "";
              const dateLabel = job.scheduledInstallDate
                ? formatShortDate(job.scheduledInstallDate)
                : "no date";
              const sub = crew
                ? `${crew} · now ${dateLabel}`
                : `Unassigned · now ${dateLabel}`;
              return (
                <JobRow
                  key={job.id}
                  jobId={job.id}
                  title={job.customerName}
                  sub={sub}
                  tag={`${count}×`}
                  tagTone="warn"
                  focusTarget={resolveFocusTarget(job.scheduledInstallDate, crew)}
                  onClose={onClose}
                />
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}

// ── First-Time-Complete: failed/incomplete wraps ─────────────────────────
function FtcDrilldown({ onClose }: { onClose: () => void }) {
  const { jobs, installerWrapRecords } = useAppData();
  const list = useMemo(() => {
    const windowStart = startOfDay(subDays(new Date(), 90));
    return installerWrapRecords
      .filter(w => {
        if (w.outcome === "completed_today") return false;
        const d = parseISO(w.timestamp);
        return isValid(d) && d >= windowStart;
      })
      .map(w => ({
        wrap: w,
        job: jobs.find(j => j.id === w.jobId),
      }))
      .filter(x => x.job)
      .sort((a, b) => b.wrap.timestamp.localeCompare(a.wrap.timestamp))
      .slice(0, 30);
  }, [installerWrapRecords, jobs]);

  return (
    <>
      <SheetHeader>
        <SheetTitle className="text-base">Returns &amp; incomplete wraps</SheetTitle>
        <SheetDescription className="text-xs">
          Wraps not closed on first visit · 90d. Counts these wrap outcomes:
          <span className="block mt-1 text-foreground/80">
            partial returning · partial reschedule · blocked issue · customer delay
          </span>
        </SheetDescription>
      </SheetHeader>
      <div className="mt-6">
        {list.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">All wraps completed first-visit. Excellent.</p>
        ) : (
          <ul className="space-y-1">
            {list.map(({ wrap, job }) => {
              const crew = job!.assignedCrew || job!.assignedSubcontractor || "";
              return (
                <JobRow
                  key={wrap.id ?? `${wrap.jobId}-${wrap.timestamp}`}
                  jobId={job!.id}
                  title={job!.customerName}
                  sub={`${job!.projectType} · ${job!.address.city}`}
                  tag={prettyOutcome(wrap.outcome)}
                  tagTone="bad"
                  focusTarget={resolveFocusTarget(job!.scheduledInstallDate, crew)}
                  onClose={onClose}
                />
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}

function prettyOutcome(o: string): string {
  return o.replace(/_/g, " ");
}

function formatShortDate(iso: string): string {
  const d = parseISO(iso);
  return isValid(d) ? format(d, "MMM d") : iso;
}

// Build the focus-grid payload from a job's scheduled state. Cell-level
// focus when both crew + date exist; crew-row fallback when only crew is
// known; no-op (with subtle toast) otherwise.
function resolveFocusTarget(
  scheduledInstallDate: string | undefined,
  crewName: string,
):
  | { kind: "cell"; crewName: string; dateIso: string }
  | { kind: "row"; crewName: string }
  | { kind: "none"; reason: string } {
  if (crewName && scheduledInstallDate) {
    const d = parseISO(scheduledInstallDate);
    if (isValid(d)) return { kind: "cell", crewName, dateIso: scheduledInstallDate };
  }
  if (crewName) return { kind: "row", crewName };
  return { kind: "none", reason: "Job is unassigned and unscheduled." };
}

// ── Shared row ───────────────────────────────────────────────────────────
function JobRow({
  jobId, title, sub, tag, tagTone, focusTarget, onClose,
}: {
  jobId: string;
  title: string;
  sub: string;
  tag: string;
  tagTone: "good" | "warn" | "bad";
  focusTarget?:
    | { kind: "cell"; crewName: string; dateIso: string }
    | { kind: "row"; crewName: string }
    | { kind: "none"; reason: string };
  onClose: () => void;
}) {
  const tagClass =
    tagTone === "bad" ? "bg-destructive/10 text-destructive" :
    tagTone === "warn" ? "bg-warning/10 text-warning" :
    "bg-success/10 text-success";
  return (
    <li className="flex items-stretch gap-1">
      <Link
        to={`/jobs/${jobId}`}
        className="flex-1 flex items-center justify-between gap-3 px-3 py-2 border border-border/40 rounded-md hover:bg-muted/30 transition-colors group min-w-0"
      >
        <div className="min-w-0">
          <p className="text-[12px] font-medium text-foreground truncate">{title}</p>
          <p className="text-[10.5px] text-muted-foreground truncate">{sub}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={cn("text-[10px] font-medium px-1.5 py-0.5 rounded capitalize", tagClass)}>
            {tag}
          </span>
          <ArrowRight className="h-3 w-3 text-muted-foreground/40 group-hover:text-foreground transition-colors" />
        </div>
      </Link>
      {focusTarget && (
        <FocusButton
          onClick={() => focusInGrid(focusTarget, onClose)}
          label={focusTarget.kind === "cell" ? "Focus scheduled cell" : focusTarget.kind === "row" ? "Focus crew row" : "Nothing to focus"}
        />
      )}
    </li>
  );
}

function FocusButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="flex items-center justify-center w-7 px-1.5 rounded-md border border-border/40 text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
    >
      <Crosshair className="h-3 w-3" />
    </button>
  );
}
